<template>
  <div class="ui-video-player" ref="wrapper">
    <!-- Restricted overlay -->
    <div v-if="restricted" class="ui-video-player__restricted" role="alert">
      <slot name="restriction">
        <div class="ui-video-player__restricted-inner">
          <p class="ui-video-player__restricted-msg">{{ restrictionMessage }}</p>
          <button
            v-if="restrictionMessage"
            class="ui-video-player__restricted-btn"
            type="button"
            @click="$emit('requestAccess')"
          >שדרגו עכשיו</button>
        </div>
      </slot>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="ui-video-player__loading" aria-busy="true" aria-label="טוען וידאו">
      <slot name="loading">
        <div class="ui-video-player__spinner" role="status"></div>
      </slot>
    </div>

    <!-- The actual video element — Video.js attaches here -->
    <video
      ref="videoEl"
      class="video-js"
      :autoplay="autoplay || null"
      :controls="controls || null"
      :poster="poster || undefined"
    ></video>
  </div>
</template>

<script>
import videojs from 'video.js';
require('video.js/dist/video-js.min.css');
import 'videojs-youtube';

export default {
  name: 'UiVideoPlayer',

  props: {
    /** Video source URL (mp4, m3u8, or YouTube) */
    src: { type: String, default: '' },
    /** Poster / thumbnail URL */
    poster: { type: String, default: '' },
    /** Start autoplay when src is set */
    autoplay: { type: Boolean, default: false },
    /** Show Video.js native controls */
    controls: { type: Boolean, default: true },
    /** When true renders restriction overlay and blocks playback */
    restricted: { type: Boolean, default: false },
    /** Text shown inside restriction overlay */
    restrictionMessage: { type: String, default: '' },
    /** Show a spinner overlay (e.g. while parent is fetching src) */
    loading: { type: Boolean, default: false },
    /** Seek to this position (seconds) after player is ready */
    startTime: { type: Number, default: 0 },
    /** Array of subtitle track objects: { name, key, url } */
    srtList: { type: Array, default: () => [] },
    /** Video.js controlBar children config override */
    controlBarChildren: { type: Array, default: null },
    /** Extra Video.js options merged with defaults */
    extraOptions: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      player: null,
    };
  },

  watch: {
    src(newSrc) {
      if (this.player && newSrc) {
        this.player.src(this._buildSource(newSrc));
        if (this.autoplay) this.player.play();
      }
    },
    poster(newPoster) {
      if (this.player && newPoster) {
        this.player.poster(newPoster);
      }
    },
  },

  mounted() {
    this._initPlayer();
  },

  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }
  },

  methods: {
    // ─── Public API (callable via $refs.player) ───────────────────────────────

    /** Seek to time in seconds */
    seek(time) {
      if (this.player) this.player.currentTime(time);
    },

    /** Play */
    play() {
      if (this.player) this.player.play();
    },

    /** Pause */
    pause() {
      if (this.player) this.player.pause();
    },

    /** Set volume 0–1 */
    setVolume(vol) {
      if (this.player) this.player.volume(vol);
    },

    /** Get current playback time in seconds */
    getCurrentTime() {
      return this.player ? this.player.currentTime() : 0;
    },

    /** Get total duration in seconds */
    getDuration() {
      return this.player ? this.player.duration() : 0;
    },

    /** Request fullscreen */
    requestFullscreen() {
      if (this.player) this.player.requestFullscreen();
    },

    /** Add a custom Video.js button to a named group.
     *  group: 'left_group' | 'center_group' | 'right_group'
     *  btnComponent: result of videojs.extend(videojs.getComponent('Button'), {...})
     *  cssClass: optional string
     */
    addControlBarButton(group, btnComponent, cssClass) {
      if (!this.player) return;
      const grp = this.player.controlBar[group];
      if (!grp) return;
      const child = grp.addChild(new btnComponent());
      if (cssClass) child.addClass(cssClass);
      return child;
    },

    // ─── Internal ────────────────────────────────────────────────────────────

    _buildSource(src) {
      if (!src) return { src: '', type: 'video/mp4' };
      let type = src.includes('m3u8') ? 'application/x-mpegURL' : 'video/mp4';
      let resolved = src;
      if (src.includes('youtube') || src.includes('youtu.be')) {
        type = 'video/youtube';
        resolved = (src + (src.includes('?') ? '&' : '?') + 'rel=0')
          .replace('https://youtu.be/', 'https://www.youtube.com/watch?v=');
      }
      return { src: resolved, type };
    },

    _buildControlBar() {
      if (this.controlBarChildren) return { children: this.controlBarChildren };
      return {
        children: [
          'progressControl',
          'CurrentTimeDisplay',
          'TimeDivider',
          'DurationDisplay',
          'ProgressControl',
          'LiveDisplay',
          'RemainingTimeDisplay',
          'CustomControlSpacer',
          'PlaybackRateMenuButton',
          'ChaptersButton',
          'DescriptionsButton',
          'AudioTrackButton',
        ],
      };
    },

    _initPlayer() {
      const self = this;

      videojs.addLanguage('en', { 'captions off': 'ללא כתוביות' });

      const options = Object.assign(
        {
          autoplay: this.autoplay,
          controls: this.controls,
          poster: this.poster,
          playbackRates: [0.5, 1, 1.5, 2],
          sources: this.src ? [this._buildSource(this.src)] : [],
          controlBar: this._buildControlBar(),
        },
        this.extraOptions
      );

      this.player = videojs(this.$refs.videoEl, options, function onReady() {
        // Add subtitles
        for (const srt of self.srtList) {
          self.player.addRemoteTextTrack({
            label: srt.name,
            kind: 'subtitles',
            language: srt.key,
            src: srt.url,
            default: true,
          });
        }

        // Seek to saved position
        if (self.startTime > 0) {
          setTimeout(() => { self.player.currentTime(self.startTime); }, 200);
        }

        // Forward standard playback events to parent
        self.player.on('play', () => self.$emit('play'));
        self.player.on('pause', () => self.$emit('pause'));
        self.player.on('ended', () => self.$emit('ended'));
        self.player.on('error', (err) => self.$emit('error', err));
        self.player.on('timeupdate', () => {
          self.$emit('timeupdate', {
            currentTime: self.player.currentTime(),
            duration: self.player.duration(),
          });
        });

        self.$emit('ready', self.player);
      });

      // Keyboard hotkeys (if plugin loaded)
      if (this.player.hotkeys && typeof this.player.hotkeys === 'function') {
        this.player.hotkeys({ volumeStep: 0.1, seekStep: 5, enableModifiersForNumbers: false });
      }

      // ── Control bar groups ─────────────────────────────────────────────────
      const isMobile = this._isMobile();

      // LEFT group
      this.player.controlBar.left_group = this.player.controlBar.addChild('Component');
      this.player.controlBar.left_group.addClass('left_group');
      this.player.controlBar.left_group.addChild('FullscreenToggle');
      if (!isMobile) this.player.controlBar.left_group.addChild('PictureInPictureToggle');
      this.player.controlBar.left_group.addChild('SubsCapsButton');
      if (!isMobile) this.player.controlBar.left_group.addChild('VolumePanel');
      this.player.controlBar.el().append(
        this.player.controlBar.left_group.el(),
        this.player.controlBar.el().firstChild.nextSibling
      );

      // CENTER group
      this.player.controlBar.center_group = this.player.controlBar.addChild('Component');
      this.player.controlBar.center_group.addClass('center_group');
      this.player.controlBar.center_group.addChild('seekButton', { direction: 'back', seconds: 10 });
      this.player.controlBar.center_group.addChild('PlayToggle');
      this.player.controlBar.center_group.addChild('seekButton', { direction: 'forward', seconds: 10 });
      this.player.controlBar.el().append(
        this.player.controlBar.center_group.el(),
        this.player.controlBar.el().firstChild.nextSibling
      );

      // RIGHT group (empty by default — parent injects episode/info buttons via addControlBarButton)
      this.player.controlBar.right_group = this.player.controlBar.addChild('Component');
      this.player.controlBar.right_group.addClass('right_group');
      this.player.controlBar.el().append(
        this.player.controlBar.right_group.el(),
        this.player.controlBar.el().firstChild.nextSibling
      );

      this.$emit('playerCreated', this.player);
    },

    _isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
  },
};
</script>

<style scoped lang="scss">
.ui-video-player {
  position: relative;
  width: 100%;
   /* 16:9 aspect ratio fallback */
  height: 0;
}

/* ── Restriction overlay ────────────────────────────────────────────── */
.ui-video-player__restricted {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.ui-video-player__restricted-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
}

.ui-video-player__restricted-msg {
  font-size: 16px;
  font-family: var(--font-hebrew, 'Heebo', sans-serif);
  color: var(--color-text, #F5F5F5);
  margin: 0;
}

.ui-video-player__restricted-btn {
  min-height: 44px;
  min-width: 44px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 24px;
  padding-right: 24px;
  background: var(--color-accent, #E11D2A);
  color: #ffffff;
  font-family: var(--font-hebrew, 'Heebo', sans-serif);
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-sm, 6px);
  cursor: pointer;
  transition: background 120ms ease;
}

.ui-video-player__restricted-btn:hover {
  background: var(--color-accentHover, #F03240);
}

.ui-video-player__restricted-btn:active {
  background: var(--color-accentPress, #C0121E);
}

.ui-video-player__restricted-btn:focus-visible {
  outline: 2px solid var(--color-accent, #E11D2A);
  outline-offset: 2px;
}

/* ── Loading overlay ────────────────────────────────────────────────── */
.ui-video-player__loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.ui-video-player__spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--color-surface4, #2A2A2A);
  border-top-color: var(--color-accent, #E11D2A);
  border-radius: 50%;
  animation: uivp-spin 0.8s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .ui-video-player__spinner {
    animation: none;
    opacity: 0.5;
  }
  .ui-video-player__restricted-btn {
    transition: none;
  }
}

@keyframes uivp-spin {
  to { transform: rotate(360deg); }
}
</style>

<style lang="scss">
/* Non-scoped: ensure video-js fills its wrapper when inside UiVideoPlayer */
.ui-video-player .video-js {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
</style>
